// Anruf von Hand protokollieren (z. B. vom Handy geführt): geht als Anruf-Aktivität nach Close.
import { ZeitFeld } from "../ZeitFeld";
import { useEffect, useState } from "react";
import { aktivitaetenApi, type Aktivitaet, type Disposition, type Kontakt } from "../../api";
import { DISPOSITION } from "../../texte";
import { textZuHtml } from "../../werkzeuge/html";
import { berlinTeile, berlinZuIso } from "../../werkzeuge/zeit";
import { Auswahl } from "../Auswahl";
import { Dialog } from "../Dialog";
import { useMeldung } from "../Meldungen";
import { telefonText } from "../Telefonnummer";

interface Props {
  offen: boolean;
  leadId: string;
  kontakte: Kontakt[];
  onSchliessen: () => void;
  onGespeichert: (a: Aktivitaet) => void;
}

const ERGEBNISSE: Disposition[] = ["answered", "no-answer", "vm-left", "vm-answer", "busy"];

export function AnrufProtokoll({ offen, leadId, kontakte, onSchliessen, onGespeichert }: Props) {
  const meldung = useMeldung();
  const [richtung, setRichtung] = useState<"outbound" | "inbound">("outbound");
  const [kontakt, setKontakt] = useState<string | null>(null);
  const [nummer, setNummer] = useState("");
  const [ergebnis, setErgebnis] = useState<Disposition>("answered");
  const [dauer, setDauer] = useState("");
  const [datum, setDatum] = useState("");
  const [zeit, setZeit] = useState("");
  const [zeitFehler, setZeitFehler] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!offen) return;
    const k = kontakte[0];
    setRichtung("outbound");
    setKontakt(k?.id ?? null);
    setNummer(k?.phones[0]?.phone ?? "");
    setErgebnis("answered");
    setDauer("");
    const jetzt = berlinTeile(new Date().toISOString());
    setDatum(jetzt.datum);
    setZeit(jetzt.zeit);
    setNotiz("");
  }, [offen, kontakte]);

  const nummern = kontakte.find((k) => k.id === kontakt)?.phones ?? [];

  const dauerSek = (): number | null => {
    const t = dauer.trim();
    if (!t) return null;
    if (t.includes(":")) {
      const [m, s] = t.split(":").map((x) => Number(x) || 0);
      return m * 60 + s;
    }
    const min = Number(t.replace(",", "."));
    return Number.isFinite(min) ? Math.round(min * 60) : null;
  };

  const speichern = async () => {
    if (zeitFehler) return; // ungültige Uhrzeit bleibt rot stehen
    setLaeuft(true);
    try {
      const r = await aktivitaetenApi.anrufProtokollieren(leadId, {
        direction: richtung,
        contact_id: kontakt,
        phone: nummer || null,
        disposition: ergebnis,
        duration: ergebnis === "answered" ? dauerSek() : 0,
        note_html: notiz.trim() ? textZuHtml(notiz) : null,
        activity_at: datum && zeit ? berlinZuIso(datum, zeit) : null,
      });
      onGespeichert(r.activity);
      meldung.ok("Anruf protokolliert.");
      onSchliessen();
    } catch (e) {
      meldung.fehler(e, "Anruf nicht protokolliert");
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog
      titel="Anruf protokollieren"
      offen={offen}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <button type="button" className="knopf knopf--leise" onClick={onSchliessen}>
            Abbrechen
          </button>
          <button type="button" className="knopf knopf--primaer" disabled={laeuft} onClick={() => void speichern()}>
            {laeuft ? "Speichert …" : "Protokollieren"}
          </button>
        </>
      }
    >
      <form
        className="formular"
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void speichern();
          }
        }}
      >
        <div className="feld">
          <span className="feld__label">Richtung</span>
          <div className="segment">
            <button type="button" className={`segment__knopf${richtung === "outbound" ? " segment__knopf--an" : ""}`} onClick={() => setRichtung("outbound")}>
              Ausgehend
            </button>
            <button type="button" className={`segment__knopf${richtung === "inbound" ? " segment__knopf--an" : ""}`} onClick={() => setRichtung("inbound")}>
              Eingehend
            </button>
          </div>
        </div>
        <div className="formular__zwei">
          <div className="feld">
            <span className="feld__label">Kontakt</span>
            <Auswahl
              optionen={kontakte.map((k) => ({ wert: k.id, label: k.name ?? "Ohne Namen", unter: k.title }))}
              wert={kontakt}
              leerErlaubt
              platzhalter="Kein Kontakt"
              onWahl={(w) => {
                const id = (w as string | null) ?? null;
                setKontakt(id);
                setNummer(kontakte.find((k) => k.id === id)?.phones[0]?.phone ?? "");
              }}
            />
          </div>
          <div className="feld">
            <span className="feld__label">Nummer</span>
            {nummern.length > 1 ? (
              <Auswahl optionen={nummern.map((p) => ({ wert: p.phone, label: telefonText(p.phone, p.phone_formatted) }))} wert={nummer} onWahl={(w) => setNummer(String(w ?? ""))} />
            ) : (
              <input className="eingabe" value={nummer} inputMode="tel" onChange={(e) => setNummer(e.target.value)} placeholder="+49 …" />
            )}
          </div>
        </div>
        <div className="feld">
          <span className="feld__label">Ergebnis</span>
          <div className="wahlknoepfe">
            {ERGEBNISSE.map((d) => (
              <button key={d} type="button" className={`wahlknopf${ergebnis === d ? " wahlknopf--an" : ""}`} onClick={() => setErgebnis(d)}>
                {DISPOSITION[d]}
              </button>
            ))}
          </div>
        </div>
        <div className="formular__zwei">
          <label className="feld">
            <span className="feld__label">Dauer (Min. oder mm:ss)</span>
            <input className="eingabe" value={dauer} disabled={ergebnis !== "answered"} onChange={(e) => setDauer(e.target.value)} placeholder="z. B. 4:30" />
          </label>
          <div className="feld">
            <span className="feld__label">Zeitpunkt</span>
            <span className="zeitpunkt">
              <input className="eingabe eingabe--datum" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} aria-label="Datum" />
              <ZeitFeld className="eingabe eingabe--uhrzeit" wert={zeit} onChange={setZeit} onUngueltig={setZeitFehler} />
            </span>
          </div>
        </div>
        <label className="feld">
          <span className="feld__label">Notiz zum Anruf</span>
          <textarea className="eingabe eingabe--text" rows={4} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </label>
      </form>
    </Dialog>
  );
}
